import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type AddAdvanceInvoiceRequest,
  type AdvancesResponse,
  formatMoney,
  fromCents,
  type InvoiceAdvance,
  type InvoiceAdvanceOptions,
  INVOICE_ADVANCE_STATE_LABELS,
  type InvoiceAdvanceState,
  invoiceAdvanceSummary,
  invoiceAdvanceTimeline,
  type InvoiceResponse,
  parseDecimal,
  type ReplacedAdvanceOption,
  type TimelineKind,
  toCents,
} from '@k-management/shared';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { DecimalInput } from '../../components/DecimalInput.js';
import { Field } from '../../components/Field.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { movementPath, treasuryKeys } from './treasury-ui.js';

const STATE_PILLS: Record<InvoiceAdvanceState, string> = {
  'in-attesa': 'info',
  'da-restituire': 'warn',
  'da-sistemare': 'bad',
  insoluto: 'bad',
  chiuso: 'ok',
};

const KINDS: Record<TimelineKind, { label: string; pill: string }> = {
  fattura: { label: 'Fattura', pill: 'muted' },
  anticipo: { label: 'Anticipo', pill: 'info' },
  incasso: { label: 'Incasso', pill: 'ok' },
  trattenuto: { label: 'Trattenuto', pill: 'ok' },
  rimborso: { label: 'Rimborso anticipo', pill: 'warn' },
  estinzione: { label: 'Estinzione', pill: 'warn' },
  insoluto: { label: 'Insoluto', pill: 'bad' },
  altro: { label: 'Altro', pill: 'muted' },
};

const signed = (amount: string | null) =>
  amount === null ? (
    <span className="muted">—</span>
  ) : (
    `${Number(amount) > 0 ? '+ ' : '− '}${formatMoney(amount.replace('-', ''))}`
  );

/**
 * The advance of the bank on an issued invoice (1.82): its state, what was
 * lent, given back and collected, and every movement of the invoice in date
 * order with its effect on the invoice and on the debt towards the bank.
 * The interest is a financial cost of the account, not of the invoice.
 * From 2.09 the advance is said from here too, and so is the collection the
 * bank kept (Intesa: the customer pays the bank, nothing on the account).
 */
export function InvoiceAdvanceSection({
  invoice,
  accountLabel,
  onSearch,
  canWrite,
}: {
  invoice: InvoiceResponse;
  accountLabel: (code: string) => string;
  /** «Cerca l’incasso in banca», when the invoice can be collected from here. */
  onSearch: (() => void) | null;
  canWrite: boolean;
}) {
  const [marking, setMarking] = useState(false);
  // Still owed to the bank while the invoice is to collect: the customer may have paid the bank.
  const retainable = invoice.advances.filter(
    (advance) => toCents(advance.open) > 0 && toCents(invoice.residual) > 0,
  );
  const options = useQuery({
    queryKey: [...treasuryKeys.invoice(invoice.id), 'advance-options'],
    queryFn: () =>
      apiRequest<InvoiceAdvanceOptions>(`/treasury/advances/for-invoice/${invoice.id}`),
    // Also for an invoice still to collect: it may be the one issued again after a credit note (2.17).
    enabled: canWrite && (marking || retainable.length > 0 || toCents(invoice.residual) > 0),
  });
  const replaced = canWrite && toCents(invoice.residual) > 0 ? (options.data?.replaced ?? []) : [];
  const replacedForms = replaced.map((option) => (
    <ReplacedRetainForm
      key={`${option.movementId}|${option.invoiceId}`}
      invoice={invoice}
      option={option}
      collectedOn={options.data?.collectedOn ?? null}
      accountLabel={accountLabel}
    />
  ));
  const markButton = canWrite && (
    <button
      type="button"
      className={invoice.advances.length ? 'small' : 'primary'}
      onClick={() => setMarking(true)}
    >
      Segna l’anticipo…
    </button>
  );
  const dialog = marking && (
    <MarkAdvanceDialog
      invoice={invoice}
      options={options.data ?? null}
      loading={options.isPending}
      error={options.error?.message ?? null}
      accountLabel={accountLabel}
      onClose={() => setMarking(false)}
    />
  );

  if (invoice.advances.length === 0) {
    if (!canWrite) return null;
    return (
      <div className="card stack">
        <div className="row between">
          <h2>Anticipo della banca</h2>
          <span className="pill muted">Non anticipata</span>
        </div>
        {replacedForms}
        <div className="row">
          {markButton}
          <span className="muted small">
            Se la banca ha anticipato la fattura: cerca i movimenti di anticipo di tutti i conti da
            un mese prima della data della fattura a 60 giorni dopo.
          </span>
        </div>
        {dialog}
      </div>
    );
  }

  const summary = invoiceAdvanceSummary(invoice, invoice.advances);
  const rows = invoiceAdvanceTimeline(invoice.advances, invoice.settlements, invoice);
  const repaidOn = invoice.advances
    .flatMap((advance) => advance.closures)
    .filter((closure) => closure.kind === 'rimborso')
    .map((closure) => closure.date)
    .sort()
    .pop();
  const tiles: { label: string; value: string; tone?: 'ok' | 'bad' }[] = [
    { label: 'Fattura', value: formatMoney(invoice.netDue) },
    {
      label: `Anticipato dalla banca${summary.percent ? ` (${summary.percent}%)` : ''}`,
      value: formatMoney(summary.advanced),
    },
    { label: 'Restituito o trattenuto dalla banca', value: formatMoney(summary.returned) },
    ...(Number(summary.unpaid) > 0
      ? [{ label: 'Ripreso dalla banca (insoluto)', value: formatMoney(summary.unpaid) }]
      : []),
    {
      label: 'Ancora dovuto alla banca',
      value: formatMoney(summary.owed),
      tone: Number(summary.owed) > 0 ? undefined : 'ok',
    },
    { label: 'Incassato dal cliente', value: formatMoney(summary.collected) },
    {
      label: 'Da incassare',
      value: formatMoney(summary.residual),
      tone:
        Number(summary.residual) > 0 ? (summary.state === 'in-attesa' ? undefined : 'bad') : 'ok',
    },
  ];
  return (
    <div className="card stack">
      <div className="row between">
        <h2>Anticipo della banca</h2>
        <div className="row">
          {markButton}
          <span className={`pill ${STATE_PILLS[summary.state]}`}>
            {INVOICE_ADVANCE_STATE_LABELS[summary.state]}
          </span>
        </div>
      </div>
      <div className="treasury-tiles">
        {tiles.map((tile) => (
          <div
            key={tile.label}
            className={`treasury-tile${tile.tone === 'bad' ? ' bad' : tile.tone === 'ok' ? ' ok' : ''}`}
          >
            <span>{tile.label}</span>
            <strong>{tile.value}</strong>
          </div>
        ))}
      </div>
      <h3>Tutti i movimenti della fattura</h3>
      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Data</th>
              <th>Cosa</th>
              <th>Conto</th>
              <th className="numeric">Fattura</th>
              <th className="numeric">Debito verso la banca</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.key}>
                <td>{formatDate(row.date)}</td>
                <td>
                  <span className={`pill ${KINDS[row.kind].pill}`}>{KINDS[row.kind].label}</span>{' '}
                  {row.text}
                  {row.batch && (
                    <small className="muted block">
                      quota di questa fattura: la distinta ha {row.batch.invoices} fatture,{' '}
                      {formatMoney(row.batch.advanced)} in tutto
                    </small>
                  )}
                </td>
                <td>
                  {row.movement ? (
                    <Link to={movementPath(row.movement.accountCode, row.movement.id)}>
                      {accountLabel(row.movement.accountCode)}
                    </Link>
                  ) : row.kind === 'fattura' ? (
                    ''
                  ) : (
                    <span className="muted">senza movimento</span>
                  )}
                </td>
                <td className="numeric">{signed(row.invoice)}</td>
                <td className="numeric">{signed(row.bank)}</td>
              </tr>
            ))}
            <tr className="advance-total">
              <td colSpan={3}>
                <strong>Saldo</strong>
              </td>
              <td className="numeric">
                <strong>{formatMoney(summary.residual)}</strong>
              </td>
              <td className="numeric">
                <strong>{formatMoney(summary.owed)}</strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {canWrite &&
        retainable.map((advance) => (
          <RetainForm
            key={advance.movementId}
            invoice={invoice}
            advance={advance}
            collectedOn={options.data?.collectedOn ?? null}
            accountLabel={accountLabel}
          />
        ))}
      {replacedForms}
      {summary.state === 'da-sistemare' && (
        <div className="notice warn row between">
          <span>
            La banca si è ripresa l’anticipo con il rimborso
            {repaidOn ? ` del ${formatDate(repaidOn)}` : ''}: di solito vuol dire che il cliente ha
            pagato, ma l’incasso non è riconciliato.
          </span>
          {onSearch && (
            <button type="button" className="primary small" onClick={onSearch}>
              Cerca l’incasso in banca
            </button>
          )}
        </div>
      )}
      {summary.state === 'insoluto' && (
        <p className="notice warn">
          Il cliente non ha pagato e la banca si è ripresa l’anticipo: la fattura va sollecitata.
          Quando paga, l’incasso arriva sul conto per intero.
        </p>
      )}
      {summary.state === 'da-restituire' && (
        <p className="notice small">
          Il cliente ha pagato: la banca addebiterà il rimborso dell’anticipo, che si abbina da solo
          quando arriva il movimento.
        </p>
      )}
      <p className="muted small">
        L’anticipo è un prestito sulla fattura, non un incasso: la fattura si chiude con il
        pagamento del cliente, il debito con il rimborso alla banca. Gli interessi non sono per
        fattura: sono oneri finanziari del conto. Vedi anche{' '}
        <Link to="/tesoreria/anticipi">Anticipi fatture</Link>.
      </p>
      {dialog}
    </div>
  );
}

/**
 * The advance of an invoice said from the invoice (2.09): the advances of
 * the banks in the 60 days after it, the one whose free part is this invoice
 * (to collect, or with the VAT as Intesa writes it) first. The invoice is
 * added to those the advance already has.
 */
function MarkAdvanceDialog({
  invoice,
  options,
  loading,
  error,
  accountLabel,
  onClose,
}: {
  invoice: InvoiceResponse;
  options: InvoiceAdvanceOptions | null;
  loading: boolean;
  error: string | null;
  accountLabel: (code: string) => string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const [chosen, setChosen] = useState<string | null>(null);
  const [presented, setPresented] = useState<string | null>(null);
  const list = options?.options ?? [];
  const selected =
    list.find((option) => option.movementId === chosen) ??
    (chosen === null ? list.find((option) => option.proposed) : undefined) ??
    null;
  const amount = presented ?? selected?.presentedFor ?? '';
  const value = parseDecimal(amount);
  const save = useMutation({
    mutationFn: () =>
      apiRequest<AdvancesResponse>(
        `/treasury/advances/${selected!.movementId}/invoices/${invoice.id}`,
        {
          method: 'POST',
          body: { presented: value!.toFixed(2) } satisfies AddAdvanceInvoiceRequest,
        },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: treasuryKeys.all });
      onClose();
    },
  });
  const withVat = toCents(invoice.total) !== toCents(invoice.netDue);
  const free = selected?.presented
    ? fromCents(toCents(selected.presented) - toCents(selected.assigned))
    : null;

  return (
    <dialog ref={dialogRef} className="dialog wide-dialog" onClose={onClose}>
      <div className="stack">
        <h2>Segna l’anticipo · fattura {invoice.number}</h2>
        <p className="muted">
          Da incassare <strong>{formatMoney(invoice.netDue)}</strong>
          {withVat && <> ({formatMoney(invoice.total)} con l’IVA)</>}. Movimenti di anticipo
          {options ? ` dal ${formatDate(options.from)} al ${formatDate(options.to)}` : ''}; se il
          movimento ha già altre fatture, questa si aggiunge e l’anticipato si divide in proporzione
          al presentato.
        </p>
        {loading && <p className="muted">Caricamento…</p>}
        {error && <p className="error">{error}</p>}
        {options && (
          <div className="table-wrap" style={{ maxHeight: '45vh' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Conto</th>
                  <th className="numeric">Anticipato</th>
                  <th className="numeric">Presentato</th>
                  <th className="numeric">Già assegnato</th>
                </tr>
              </thead>
              <tbody>
                {list.map((option) => (
                  <tr key={option.movementId}>
                    <td>
                      <label className="checkbox">
                        <input
                          type="radio"
                          name="advance"
                          checked={selected?.movementId === option.movementId}
                          onChange={() => {
                            setChosen(option.movementId);
                            setPresented(null);
                          }}
                        />{' '}
                        {formatDate(option.date)}
                      </label>
                      {option.proposed && (
                        <span className="pill info">
                          {toCents(option.presentedFor) === toCents(invoice.netDue)
                            ? 'presentato = fattura'
                            : 'presentato = fattura con l’IVA'}
                        </span>
                      )}
                    </td>
                    <td>
                      <Link
                        to={movementPath(option.accountCode, option.movementId)}
                        target="_blank"
                      >
                        {accountLabel(option.accountCode)}
                      </Link>
                      <small className="muted block">
                        {option.reference ?? (option.followUp ? 'Anticipo successivo' : 'Anticipo')}
                      </small>
                    </td>
                    <td className="numeric">{formatMoney(option.advanced)}</td>
                    <td className="numeric">
                      {option.presented ? formatMoney(option.presented) : '—'}
                    </td>
                    <td className="numeric">
                      {option.invoices > 0 ? (
                        <>
                          {formatMoney(option.assigned)}
                          <small className="muted block">
                            {option.invoices} {option.invoices === 1 ? 'fattura' : 'fatture'}
                          </small>
                        </>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
                {list.length === 0 && (
                  <tr>
                    <td colSpan={5} className="muted">
                      Nessun movimento di anticipo dal mese prima della fattura a 60 giorni dopo:
                      forse l’estratto conto di quel periodo non è caricato.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
        {selected && (
          <Field
            label="Presentato per questa fattura"
            hint={free ? `Ancora libero nell’anticipo: ${formatMoney(free)}.` : undefined}
          >
            <DecimalInput value={amount} onChange={setPresented} decimals={[2, 2]} />
          </Field>
        )}
        {save.error && <p className="error">{save.error.message}</p>}
        <div className="actions">
          <span className="grid-spacer" />
          <button type="button" onClick={onClose}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={!selected || !value || save.isPending}
            onClick={() => save.mutate()}
          >
            Segna l’anticipo
          </button>
        </div>
      </div>
    </dialog>
  );
}

/**
 * The customer paid the bank, which kept what it had advanced (Intesa): a
 * collection of the invoice without a movement, said from the invoice (2.09).
 * The day proposed: the ledger of the customer, else the due date.
 */
function RetainForm({
  invoice,
  advance,
  collectedOn,
  accountLabel,
}: {
  invoice: InvoiceResponse;
  advance: InvoiceAdvance;
  collectedOn: string | null;
  accountLabel: (code: string) => string;
}) {
  const queryClient = useQueryClient();
  const proposedDate = collectedOn ?? invoice.nextDue ?? advance.date;
  const [date, setDate] = useState<string | null>(null);
  const [amount, setAmount] = useState(
    fromCents(Math.min(toCents(advance.open), toCents(invoice.residual))),
  );
  const value = parseDecimal(amount);
  const day = date ?? proposedDate;
  const save = useMutation({
    mutationFn: () =>
      apiRequest<AdvancesResponse>(
        `/treasury/advances/${advance.movementId}/invoices/${invoice.id}/retained`,
        { method: 'POST', body: { date: day, amount: value!.toFixed(2) } },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: treasuryKeys.all });
    },
  });
  return (
    <div className="notice stack">
      <span>
        <strong>Il cliente ha pagato la banca?</strong> Con l’anticipo del{' '}
        {formatDate(advance.date)} ({accountLabel(advance.accountCode)}) il cliente può pagare
        direttamente la banca, che trattiene l’anticipo: sul conto arriva solo l’eventuale
        differenza. Segna qui l’incasso trattenuto, senza movimento.
      </span>
      {/* The hint under the row: inside the field it pushed the date above the amount. */}
      <div className="row" style={{ alignItems: 'end' }}>
        <Field label="Pagato dal cliente alla banca il">
          <input type="date" value={day} onChange={(event) => setDate(event.target.value)} />
        </Field>
        <Field label="Trattenuto dalla banca">
          <DecimalInput value={amount} onChange={setAmount} decimals={[2, 2]} />
        </Field>
        <button
          type="button"
          className="primary"
          disabled={!value || !day || save.isPending}
          onClick={() => save.mutate()}
        >
          Incassata dalla banca
        </button>
      </div>
      <small className="muted">
        {collectedOn
          ? 'Data dal mastrino del cliente («Incasso Ft …»).'
          : 'Nel mastrino non c’è l’incasso: proposta la scadenza.'}
      </small>
      {save.error && <p className="error">{save.error.message}</p>}
    </div>
  );
}

/**
 * An invoice issued again after a credit note stornò one advanced by the bank
 * (2.17): the customer paid this one to the bank, which kept it for the
 * advance of the one stornata. The collection stays here and closes there.
 */
function ReplacedRetainForm({
  invoice,
  option,
  collectedOn,
  accountLabel,
}: {
  invoice: InvoiceResponse;
  option: ReplacedAdvanceOption;
  collectedOn: string | null;
  accountLabel: (code: string) => string;
}) {
  const queryClient = useQueryClient();
  const proposedDate = collectedOn ?? invoice.nextDue ?? invoice.date;
  const [date, setDate] = useState<string | null>(null);
  const [amount, setAmount] = useState(
    fromCents(Math.min(toCents(option.open), toCents(invoice.residual))),
  );
  const value = parseDecimal(amount);
  const day = date ?? proposedDate;
  const rest = value === null ? 0 : toCents(option.open) - Math.round(value * 100);
  const save = useMutation({
    mutationFn: () =>
      apiRequest<AdvancesResponse>(
        `/treasury/advances/${option.movementId}/invoices/${option.invoiceId}/retained`,
        {
          method: 'POST',
          body: { collectedInvoiceId: invoice.id, date: day, amount: value!.toFixed(2) },
        },
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: treasuryKeys.all });
    },
  });
  const notes = option.creditNotes
    .map((note) => `NC ${note.number} del ${formatDate(note.date)}`)
    .join(', ');
  return (
    <div className="notice info stack">
      <span>
        La{' '}
        <Link to={`/tesoreria/fatture/${option.invoiceId}`}>
          FT {option.number} del {formatDate(option.invoiceDate)}
        </Link>
        , stornata con la {notes}, era anticipata da {accountLabel(option.accountCode)} il{' '}
        {formatDate(option.date)}: ancora dovuti alla banca{' '}
        <strong>{formatMoney(option.open)}</strong>. Se il cliente ha pagato questa fattura alla
        banca, che l’ha trattenuta per quell’anticipo, segna qui l’incasso: resta su questa fattura
        e chiude l’anticipo della FT {option.number}.
      </span>
      <div className="row" style={{ alignItems: 'end' }}>
        <Field label="Pagato dal cliente alla banca il">
          <input type="date" value={day} onChange={(event) => setDate(event.target.value)} />
        </Field>
        <Field label="Trattenuto dalla banca">
          <DecimalInput value={amount} onChange={setAmount} decimals={[2, 2]} />
        </Field>
        <button
          type="button"
          className="primary"
          disabled={!value || !day || save.isPending}
          onClick={() => save.mutate()}
        >
          Incassata dalla banca per l’anticipo della FT {option.number}
        </button>
      </div>
      <small className="muted">
        {collectedOn
          ? 'Data dal mastrino del cliente («Incasso Ft …»).'
          : 'Nel mastrino non c’è l’incasso: proposta la scadenza.'}
        {rest > 0 &&
          ` Restano dovuti alla banca ${formatMoney(fromCents(rest))}: una decurtazione di pari importo, dopo questa data, li chiude da sola.`}
      </small>
      {save.error && <p className="error">{save.error.message}</p>}
    </div>
  );
}
